import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, MapPin, Users, ArrowRight } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip } from 'recharts';
// Radar chart data
const radarData = [{
  category: 'Preis-Leistung',
  EDEKA: 78,
  REWE: 72,
  LIDL: 85
}, {
  category: 'Frische',
  EDEKA: 88,
  REWE: 82,
  LIDL: 68
}, {
  category: 'Freundlichkeit',
  EDEKA: 82,
  REWE: 79,
  LIDL: 65
}, {
  category: 'Sauberkeit',
  EDEKA: 85,
  REWE: 83,
  LIDL: 75
}, {
  category: 'Auswahl',
  EDEKA: 90,
  REWE: 85,
  LIDL: 70
}, {
  category: 'Erreichbarkeit',
  EDEKA: 75,
  REWE: 80,
  LIDL: 88
}];
// Win/Loss data
const competitorAnalysis = [{
  location: 'Hamburg-Hafencity',
  edeka: 'EDEKA Center Hamburg',
  competitor: 'REWE City',
  competitorType: 'REWE',
  distance: '0.8 km',
  status: 'winning',
  edekaRating: 4.6,
  competitorRating: 4.2,
  insight: 'Bessere Frische-Bewertungen und Auswahl',
  customerFlow: '+12%'
}, {
  location: 'Berlin-Kreuzberg',
  edeka: 'EDEKA Neukauf Berlin',
  competitor: 'LIDL',
  competitorType: 'LIDL',
  distance: '0.3 km',
  status: 'losing',
  edekaRating: 3.8,
  competitorRating: 4.1,
  insight: 'Preis-Leistung und Wartezeiten problematisch',
  customerFlow: '-8%'
}, {
  location: 'München-Schwabing',
  edeka: 'EDEKA Center München',
  competitor: 'REWE',
  competitorType: 'REWE',
  distance: '1.2 km',
  status: 'winning',
  edekaRating: 4.8,
  competitorRating: 4.3,
  insight: 'Premium-Sortiment und Service überzeugen',
  customerFlow: '+18%'
}, {
  location: 'Köln-Ehrenfeld',
  edeka: 'EDEKA Köln',
  competitor: 'Aldi Süd',
  competitorType: 'ALDI',
  distance: '0.5 km',
  status: 'losing',
  edekaRating: 3.2,
  competitorRating: 4.0,
  insight: 'Sauberkeit und Personalfreundlichkeit verbessern',
  customerFlow: '-15%'
}, {
  location: 'Frankfurt-Sachsenhausen',
  edeka: 'EDEKA Frankfurt',
  competitor: 'REWE',
  competitorType: 'REWE',
  distance: '0.6 km',
  status: 'neutral',
  edekaRating: 4.2,
  competitorRating: 4.2,
  insight: 'Gleichstand - Differenzierung durch Bio-Sortiment möglich',
  customerFlow: '0%'
}, {
  location: 'Düsseldorf-Bilk',
  edeka: 'EDEKA Düsseldorf',
  competitor: 'LIDL',
  competitorType: 'LIDL',
  distance: '0.4 km',
  status: 'winning',
  edekaRating: 4.3,
  competitorRating: 3.9,
  insight: 'Qualität und Beratung als Stärken',
  customerFlow: '+7%'
}];
const competitorColors: Record<string, string> = {
  REWE: '#cc071e',
  LIDL: '#0050aa',
  ALDI: '#00005f'
};
export function CompetitorRadarPage() {
  const winningCount = competitorAnalysis.filter(c => c.status === 'winning').length;
  const losingCount = competitorAnalysis.filter(c => c.status === 'losing').length;
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-394">
      {/* Header KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-395">
        <KPICard title="Marktposition" value="#2" change="stabil" trend="up" icon={<Target size={18} data-id="element-397" />} subtext="in Deutschland" data-id="element-396" />
        <KPICard title="Gewonnene Standorte" value={winningCount} change={`von ${competitorAnalysis.length}`} trend="up" icon={<TrendingUp size={18} data-id="element-399" />} subtext="im 5km Radius" data-id="element-398" />
        <KPICard title="Verlorene Standorte" value={losingCount} change="Handlungsbedarf" trend="down" icon={<TrendingDown size={18} data-id="element-401" />} subtext="an Konkurrenz" data-id="element-400" />
        <KPICard title="Ø Rating-Vorsprung" value="+0.3" change="vs. Wettbewerb" trend="up" icon={<Users size={18} data-id="element-403" />} subtext="Sterne" data-id="element-402" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-404">
        {/* Radar Chart */}
        <Card data-id="element-405">
          <div className="p-6" data-id="element-406">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-407">
              Wettbewerbsvergleich
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-408">
              Kategorien-Analyse aus Google-Bewertungen
            </p>

            <div className="h-80" data-id="element-409">
              <ResponsiveContainer width="100%" height="100%" data-id="element-410">
                <RadarChart data={radarData} data-id="element-411">
                  <PolarGrid stroke="#e5e7eb" data-id="element-412" />
                  <PolarAngleAxis dataKey="category" tick={{
                  fontSize: 11,
                  fill: '#6b7280'
                }} data-id="element-413" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} data-id="element-414" />
                  <Radar name="EDEKA" dataKey="EDEKA" stroke="#003051" fill="#003051" fillOpacity={0.3} strokeWidth={2} data-id="element-415" />
                  <Radar name="REWE" dataKey="REWE" stroke="#cc071e" fill="#cc071e" fillOpacity={0.1} strokeWidth={2} data-id="element-416" />
                  <Radar name="LIDL" dataKey="LIDL" stroke="#0050aa" fill="#0050aa" fillOpacity={0.1} strokeWidth={2} data-id="element-417" />
                  <Legend wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-418" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} data-id="element-419" />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Legend explanation */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-id="element-420">
              <div className="text-xs text-gray-500 mb-2" data-id="element-421">
                Stärken von EDEKA:
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-422">
                <Badge variant="success" data-id="element-423">Frische: #1</Badge>
                <Badge variant="success" data-id="element-424">Auswahl: #1</Badge>
                <Badge variant="info" data-id="element-425">Sauberkeit: #1</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-3 mb-2" data-id="element-426">
                Verbesserungspotenzial:
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-427">
                <Badge variant="warning" data-id="element-428">Preis-Leistung: #3</Badge>
                <Badge variant="warning" data-id="element-429">Erreichbarkeit: #3</Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Category Breakdown */}
        <Card data-id="element-430">
          <div className="p-6" data-id="element-431">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-432">
              Kategorie-Details
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-433">
              Detaillierter Vergleich nach Bewertungskategorie
            </p>

            <div className="space-y-4" data-id="element-434">
              {radarData.map((item, index) => {
              const maxScore = Math.max(item.EDEKA, item.REWE, item.LIDL);
              const isEdekaLeading = item.EDEKA === maxScore;
              return <motion.div key={item.category} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="space-y-2" data-id="element-435">
                    <div className="flex items-center justify-between" data-id="element-436">
                      <span className="text-sm font-medium text-gray-700" data-id="element-437">
                        {item.category}
                      </span>
                      {isEdekaLeading && <Badge variant="success" className="text-xs" data-id="element-438">
                          Führend
                        </Badge>}
                    </div>
                    <div className="flex gap-2 h-6" data-id="element-439">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-440">
                        <div className="h-full bg-edeka-blue rounded-full transition-all duration-500" style={{
                      width: `${item.EDEKA}%`
                    }} data-id="element-441" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600" data-id="element-442">
                          {item.EDEKA}
                        </span>
                      </div>
                      <div className="w-16 text-xs text-gray-500 flex items-center" data-id="element-443">
                        EDEKA
                      </div>
                    </div>
                    <div className="flex gap-2 h-4" data-id="element-444">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-445">
                        <div className="h-full bg-red-500 rounded-full opacity-60" style={{
                      width: `${item.REWE}%`
                    }} data-id="element-446" />
                      </div>
                      <div className="w-16 text-xs text-gray-400" data-id="element-447">
                        REWE {item.REWE}
                      </div>
                    </div>
                    <div className="flex gap-2 h-4" data-id="element-448">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-449">
                        <div className="h-full bg-blue-500 rounded-full opacity-60" style={{
                      width: `${item.LIDL}%`
                    }} data-id="element-450" />
                      </div>
                      <div className="w-16 text-xs text-gray-400" data-id="element-451">
                        LIDL {item.LIDL}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </Card>
      </div>

      {/* Win/Loss Table */}
      <Card data-id="element-452">
        <div className="p-6" data-id="element-453">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-454">
            Standort-Wettbewerb
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-455">
            Wo gewinnen oder verlieren wir Kunden im 5km-Radius?
          </p>

          <div className="overflow-x-auto" data-id="element-456">
            <table className="w-full" data-id="element-457">
              <thead data-id="element-458">
                <tr className="border-b border-gray-200" data-id="element-459">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-460">
                    Standort
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-461">
                    EDEKA Markt
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-462">
                    Konkurrent
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-463">
                    Entfernung
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-464">
                    Ratings
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-465">
                    Kundenfluss
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-466">
                    Insight
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-467">
                {competitorAnalysis.map((item, index) => <motion.tr key={item.location} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className={`border-b border-gray-100 ${item.status === 'losing' ? 'bg-red-50/50' : item.status === 'winning' ? 'bg-green-50/50' : ''}`} data-id="element-468">
                    <td className="py-4 px-4" data-id="element-469">
                      <div className="flex items-center gap-2" data-id="element-470">
                        <MapPin size={14} className="text-gray-400" data-id="element-471" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-472">
                          {item.location}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4" data-id="element-473">
                      <span className="text-sm text-gray-700" data-id="element-474">
                        {item.edeka}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-475">
                      <div className="flex items-center gap-2" data-id="element-476">
                        <div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: competitorColors[item.competitorType] || '#666'
                    }} data-id="element-477" />
                        <span className="text-sm text-gray-700" data-id="element-478">
                          {item.competitor}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-479">
                      <span className="text-sm text-gray-500" data-id="element-480">
                        {item.distance}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-481">
                      <div className="flex items-center justify-center gap-2" data-id="element-482">
                        <span className="text-sm font-semibold text-edeka-blue" data-id="element-483">
                          {item.edekaRating}
                        </span>
                        <ArrowRight size={14} className="text-gray-300" data-id="element-484" />
                        <span className="text-sm text-gray-500" data-id="element-485">
                          {item.competitorRating}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-486">
                      <span className={`text-sm font-semibold ${item.status === 'winning' ? 'text-green-600' : item.status === 'losing' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-487">
                        {item.customerFlow}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-488">
                      <span className="text-xs text-gray-500" data-id="element-489">
                        {item.insight}
                      </span>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}